import React, { useState } from "react";
import { Eye, Code, Code2, Check, PanelLeft, Loader } from "lucide-react";
import { BlockPatternPreview } from "./BlockPatternPreview";
import { CodeOutput } from "./CodeOutput";
import { ProgressTask } from "../../types/pattern";

interface PreviewPanelProps {
  pattern: string;
  viewMode: "preview" | "code";
  onChangeViewMode: (mode: "preview" | "code") => void;
  onChangePattern: (pattern: string) => void;
  onCopyPattern: () => void;
  copySuccess: boolean;
  isGenerating: boolean;
  progressTasks: ProgressTask[];
  progress: number;
  progressMessage: string;
  onToggleChatVisibility: () => void;
  isChatVisible: boolean;
}

export function PreviewPanel({
  pattern,
  viewMode,
  onChangeViewMode,
  onChangePattern,
  onCopyPattern,
  copySuccess,
  isGenerating,
  progressTasks,
  progress,
  progressMessage,
  onToggleChatVisibility,
  isChatVisible,
}: PreviewPanelProps) {
  return (
    <div className="flex-1 flex flex-col bg-[#0d1117] overflow-hidden">
      {/* Preview header */}
      <div className="p-4 border-b border-[#30363d] flex items-center justify-between bg-[#161b22]">
        <div className="flex items-center">
          {!isChatVisible && (
            <button
              onClick={onToggleChatVisibility}
              className="p-2 mr-2 rounded-lg text-gray-400 hover:text-[#58a6ff] hover:bg-[#1f6feb]/10 transition-colors"
              title="Show chat"
            >
              <PanelLeft className="h-4 w-4" />
            </button>
          )}
          <h2 className="text-lg font-medium text-white">Pattern Preview</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-[#0d1117] rounded-lg overflow-hidden">
            <button
              onClick={() => onChangeViewMode("preview")}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                viewMode === "preview"
                  ? "text-[#3fb950] bg-[#1f6feb]/20"
                  : "text-white hover:text-[#3fb950]"
              }`}
            >
              <Eye className="h-4 w-4" />
              Preview
            </button>
            <button
              onClick={() => onChangeViewMode("code")}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                viewMode === "code"
                  ? "text-[#3fb950] bg-[#1f6feb]/20"
                  : "text-white hover:text-[#3fb950]"
              }`}
            >
              <Code className="h-4 w-4" />
              Code
            </button>
          </div>
          <button
            className={`inline-flex items-center px-4 py-2 font-medium rounded-lg transition-colors ${
              copySuccess
                ? "bg-[#1f6feb] text-white"
                : "bg-[#238636] text-white hover:bg-[#2ea043]"
            }`}
            onClick={onCopyPattern}
            disabled={!pattern}
          >
            {copySuccess ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Code2 className="h-4 w-4 mr-2" />
                Copy Pattern
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview content */}
      <div className="flex-1 overflow-auto p-4 bg-[#0d1117]">
        <div className="h-full bg-white rounded-lg relative">
          {viewMode === "preview" ? (
            <BlockPatternPreview pattern={pattern} />
          ) : (
            <CodeOutput
              code={pattern}
              onChange={onChangePattern}
              hideControls={true}
            />
          )}

          {/* Progress overlay */}
          {isGenerating && (
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="bg-[#161b22] p-6 rounded-xl shadow-xl max-w-md w-full">
                <h3 className="text-xl font-semibold text-white mb-4">
                  Generating Pattern
                </h3>

                <div className="space-y-4">
                  {progressTasks.map((task) => (
                    <div key={task.id} className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-6">
                        {task.status === "completed" ? (
                          <Check className="h-5 w-5 text-[#3fb950]" />
                        ) : task.status === "in-progress" ? (
                          <Loader className="h-5 w-5 text-[#58a6ff] animate-spin" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border border-gray-600" />
                        )}
                      </div>
                      <span
                        className={`text-sm ${
                          task.status === "completed"
                            ? "text-[#3fb950]"
                            : task.status === "in-progress"
                            ? "text-[#58a6ff]"
                            : "text-gray-400"
                        }`}
                      >
                        {task.name}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  <div className="w-full bg-gray-700 rounded-full h-1.5 mb-2">
                    <div
                      className="bg-[#1f6feb] h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-400">{progressMessage}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
